import type {
  CleanState,
  FormState,
  RelationshipType,
  RequestType
} from 'typings'
import config from 'const/config'
import { isCalifornia, isWashington } from './states'

export const isNotIE =
  window.navigator.userAgent.indexOf('MSE') === -1 &&
  window.navigator.userAgent.indexOf('Trident/') === -1

/**
 * Properties to send to API
 */
export const payloadProps = [
  'cipher.accountId',
  'cipher.addressLine1',
  'cipher.addressLine2',
  'cipher.city',
  'cipher.country',
  'cipher.dateOfBirth',
  'cipher.emailAddress',
  'cipher.firstName',
  'cipher.language',
  'cipher.lastName',
  'cipher.primaryPhone',
  'cipher.primaryPhoneType',
  'cipher.state',
  'cipher.zipCode',
  'activeAccountFound',
  'channelId',
  'isADACompliance',
  'mailPrefered',
  'recaptchaToken',
  'relationshipType',
  'requestorType',
  'requestType',
  'userVerifiedStatus'
]

export const getRelationshipEntity = (state: FormState): RelationshipType[] => {
  const consumerAccount = 'consumerAccount'
  const relationshipType: RelationshipType[] = []

  // business commercial account
  if (state.primaryAccountPayable.value) {
    relationshipType.push({
      type: 'Business_CommercialAccount',
      subType: state.primaryAccountPayable.value.toLowerCase(),
      'cipher.details':
        state.primaryAccountPayable.value === 'Yes'
          ? state.businessCommercialAccount
          : ''
    })
  }

  // employee applicant
  if (state.employee.value) {
    relationshipType.push({
      type: 'Employee_Applicant',
      subType: state.employee.value.toLowerCase(),
      'cipher.details': ''
    })
  }

  // user has a relationship with Synchrony
  if (state.relationshipSynchrony?.value === 'Yes') {
    if (state.creditCardChecked) {
      relationshipType.push({
        type: consumerAccount,
        subType: 'creditAccount',
        'cipher.details': state.creditCard
      })
    }
    if (state.savingAccountChecked) {
      relationshipType.push({
        type: consumerAccount,
        subType: 'savingsAccount',
        'cipher.details': state.savingProduct
      })
    }
    if (state.installmentAccountChecked) {
      relationshipType.push({
        type: consumerAccount,
        subType: 'installmentAccount',
        'cipher.details': state.installmentAccount
      })
    }
  }

  // additional notes only when no known relationship
  if (state.unsureRelationship) {
    relationshipType.push({
      type: 'additionalInformation',
      subType: '',
      'cipher.details': state.additionalNotes || 'na'
    })
  }
  return relationshipType
}

export const getRequestTypeEntity = (state: FormState): RequestType[] => {
  const requestType: RequestType[] = []
  // Flags can outlive a state change, so gate each right on the selected state.
  const ca = isCalifornia(state.state.value)
  const wa = isWashington(state.state.value)
  // access and erasure are offered by both states
  if (state.accessPersonalInfo) {
    requestType.push({
      type: 'accessToPersonalInformation',
      options: []
    })
  }
  if (state.erasurePersonalInfo) {
    requestType.push({
      type: 'erasureOfPersonalInformation',
      options: []
    })
  }
  // it has multiple options
  if (ca && state.rightToRectification) {
    requestType.push({
      type: 'rightToRectification',
      options: state.rectification.map(option => option.value)
    })
  }
  if (ca && state.rightToOptOutOfSharing) {
    requestType.push({
      type: 'rightToOptOutOfSharing',
      options: []
    })
  }
  if (ca && state.rightToOptOutOfUseOfSensitivePII) {
    requestType.push({
      type: 'rightToOptOutOfUseOfSensitivePII',
      options: []
    })
  }
  if (ca && state.rightToTransparencyForProfiling) {
    requestType.push({
      type: 'rightToTransparencyForProfiling',
      options: []
    })
  }
  if (wa && state.rightToRevokeConsent) {
    requestType.push({
      type: 'rightToRevokeConsent',
      options: []
    })
  }
  if (wa && state.rightToAppeal) {
    requestType.push({
      type: 'rightToAppeal',
      options: []
    })
  }
  return requestType
}

/** Mirrors the rights each state's form renders (California.tsx / Washington.tsx). */
export const getRightsResetForState = (
  stateValue: string
): Partial<FormState> => {
  if (isWashington(stateValue)) {
    return {
      rightToRectification: false,
      rectification: [],
      rightToOptOutOfSharing: false,
      rightToOptOutOfUseOfSensitivePII: false,
      rightToTransparencyForProfiling: false
    }
  }
  if (isCalifornia(stateValue)) {
    return {
      rightToRevokeConsent: false,
      rightToAppeal: false
    }
  }
  return {}
}

export const formatDate = (
  dateOfBirth: Date | null,
  apiFormat = false
): string => {
  if (dateOfBirth instanceof Date) {
    const year = dateOfBirth.getFullYear()
    const month = (dateOfBirth.getMonth() + 1).toString().padStart(2, '0')
    const day = dateOfBirth.getDate().toString().padStart(2, '0')
    return apiFormat ? `${year}-${month}-${day}` : `${month}/${day}/${year}`
  }
  return ''
}

/**
 * Get a payload properly, cleanup some props
 * @param state component state to cleanup
 * @returns payload
 */
export const getPayload = (
  state: FormState,
  recaptchaToken = '',
  otpToken = ''
): Partial<FormState> & {
  [key: string]: string | boolean | RequestType[] | RelationshipType[]
} => {
  // check request type
  const requestType = getRequestTypeEntity(state)
  // check relationship
  const relationshipType = getRelationshipEntity(state)
  return {
    'cipher.accountId': '',
    'cipher.firstName': state.firstName?.toUpperCase(),
    'cipher.lastName': state.lastName?.toUpperCase(),
    'cipher.dateOfBirth': formatDate(state.dateOfBirth, true),
    'cipher.country': state.country.value,
    'cipher.addressLine1': state.address?.toUpperCase(),
    'cipher.addressLine2': state.secondaryAddress?.toUpperCase(),
    'cipher.city': state.city?.toUpperCase(),
    'cipher.state': state.state.value,
    'cipher.zipCode': state.zipCode,
    userVerifiedStatus: !!otpToken,
    activeAccountFound:
      isWashington(state.state.value) &&
      state.relationshipSynchrony.value === 'Yes',
    'cipher.primaryPhone': state.phone,
    'cipher.primaryPhoneType': state.phoneType.value,
    'cipher.emailAddress': state.emailAddress?.toUpperCase(),
    isADACompliance: state.hardOfHearing,
    'cipher.language': state.language,
    mailPrefered: state.mailPreferred,
    recaptchaToken,
    channelId: config.CHANNEL_ID,
    requestorType: 'Primary',
    requestType,
    relationshipType
  }
}

export const getCleanState = (
  propName: string,
  state: FormState
): CleanState => {
  const result: CleanState = {
    checked: false,
    state: { ...state }
  }
  // clean up when uncheck
  switch (propName) {
    case 'creditCardChecked':
      result.checked = !state.creditCardChecked
      if (result.checked) return result
      result.state.creditCard = ''
      break
    case 'savingAccountChecked':
      result.checked = !state.savingAccountChecked
      if (result.checked) return result
      result.state.savingProduct = ''
      break
    case 'installmentAccountChecked':
      result.checked = !state.installmentAccountChecked
      if (result.checked) return result
      result.state.installmentAccount = ''
      break
    case 'businessCommercialAccountChecked':
      result.checked = !state.businessCommercialAccountChecked
      if (result.checked) return result
      result.state = {
        ...state,
        primaryAccountPayable: { label: '', value: '' },
        businessCommercialAccount: '',
        errors: {
          ...state.errors,
          businessCommercialAccount: ''
        }
      }
      break
    default:
      return result
  }
  return result
}

export const placeholderFunction = (): void => {
  // Placeholder function for unused but required input properties
  // As this functions are handler by the parent element or unhandled
}

const isValidCharacterDate = (
  lastCharacter: string,
  length: number
): boolean => {
  // sample date length mm/dd/yyyy [1][2]/[4][5]/[7][8][9][10]
  switch (length) {
    case 1:
      return /[0|1]/.test(lastCharacter)
    // commented because case 2 and 3 will never happen
    // due to how is used in function below
    // case 2:
    // case 5:
    case 8:
    case 9:
    case 10:
      return /[0-9]/.test(lastCharacter)
    case 4:
      return /[0-3]/.test(lastCharacter)
    case 7:
      return /[1|2]/.test(lastCharacter)
    default:
      return false
  }
}

/**
 * Help to add '/' to string
 * @param value string value
 * @returns new string
 */
export const formatTextDateInput = (value: string): string => {
  const { length } = value
  const lastCharacter = value.substring(length - 1, length)
  switch (length) {
    case 2:
    case 5:
      return lastCharacter === '/'
        ? value.substring(0, length - 1)
        : `${value}/`
    case 3:
    case 6:
      return value
    default: {
      const isValid = isValidCharacterDate(lastCharacter, length)
      return isValid ? value : value.substring(0, length - 1)
    }
  }
}
